import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

// ── Types ──────────────────────────────────────────────────────────
export type MarksSubCategory = { _id: string; description: string; marks: number }

export type MarksSubject = {
  courseStructureId: string
  course: { _id: string; name?: string; title?: string; courseIdentifier?: string } | null
  totalMarks: number
  allowSum: boolean
  subCategories: MarksSubCategory[]
  // Present only for classes split into streams (e.g. Biology/Computer);
  // omitted/null for the common ungrouped case. Backend already returns it
  // (services/marksGrid.js listSubjects), just wasn't typed through here.
  classGroup?: { _id: string; name: string } | null
}

export type MarksCell = { obtainedMarks: number; isAbsent: boolean; isNa: boolean } | null

export type MarksGridStudent = {
  sNo: number
  enrollmentId: string
  grNo: string
  rollNo: string | number
  studentName: string
  cells: Record<string, MarksCell> // keyed by subCategory _id or "__term__"
}

export type MarksGrid = {
  awardSlipId: string
  status: string
  locked: boolean
  course: { _id: string; name?: string; title?: string; courseIdentifier?: string } | null
  totalMarks: number
  subCategories: MarksSubCategory[]
  students: MarksGridStudent[]
  examinerName: string
  dateConducted: string | null
  submitted: boolean
  submittedAt: string | null
  submittedByName: string | null
}

export type SaveMarksEntry = {
  enrollmentId: string
  subCategory: string | null
  obtainedMarks: number | string | null
  isAbsent: boolean
  isNa: boolean
}

const BASE = "/marks-entry"

// List subjects (course structures) for a (session, class, section, breakdown).
export const listMarksSubjects = async (params: {
  session: string
  class: string
  section: string
  breakdown: string
  classGroup?: string
}): Promise<{ success: boolean; data: MarksSubject[] }> => {
  const res = await apiClient({
    method: "GET",
    url: appendQueryParams(`${BASE}/grid/subjects`, params),
  })
  return res.data
}

export type BreakdownSubject = {
  courseStructureId: string
  label: string
  className: string
  sectionName: string
  subject: string
  totalMarks: number
}

// List course structures (class + subject) under a breakdown — Final Term picker.
// The breakdown already pins the class, so no separate class/section picker.
export const listBreakdownSubjects = async (
  breakdownId: string
): Promise<{ success: boolean; data: BreakdownSubject[] }> => {
  const res = await apiClient({
    method: "GET",
    url: appendQueryParams(`${BASE}/breakdown-subjects`, { breakdownId }),
  })
  return res.data
}

// Build the grid for one subject. Finds-or-creates the award slip server-side.
export const getMarksGrid = async (params: {
  session: string
  class: string
  section: string
  breakdown: string
  courseStructureId: string
}): Promise<{ success: boolean; data: MarksGrid }> => {
  const res = await apiClient({
    method: "GET",
    url: appendQueryParams(`${BASE}/grid`, params),
  })
  return res.data
}

// Save grid edits.
export const saveMarksGrid = async (data: {
  awardSlipId: string
  entries: SaveMarksEntry[]
}): Promise<{ success: boolean; message: string; data: { saved: number } }> => {
  const res = await apiClient({ method: "POST", url: `${BASE}/grid/save`, data })
  return res.data
}

// Approve all marks for an award slip (assessment) — also computes positions.
export const approveAwardSlipMarks = async (assessmentId: string) => {
  const res = await apiClient({ method: "POST", url: `${BASE}/bulk-approve`, data: { assessmentId } })
  return res.data
}

// Teacher submits the grid: locks it for editing and records examiner/date-conducted.
export const submitMarksGrid = async (data: {
  awardSlipId: string
  examinerName?: string
  dateConducted?: string
}) => {
  const res = await apiClient({ method: "POST", url: `${BASE}/grid/submit`, data })
  return res.data
}

// Admin override: reopens a submitted grid for editing.
export const unsubmitMarksGrid = async (awardSlipId: string) => {
  const res = await apiClient({ method: "POST", url: `${BASE}/grid/unsubmit`, data: { awardSlipId } })
  return res.data
}

// Standalone save of examiner name and conducted date.
export const saveExamMeta = async (data: {
  awardSlipId: string
  examinerName?: string
  dateConducted?: string | null
}) => {
  const res = await apiClient({ method: "POST", url: `${BASE}/grid/meta`, data })
  return res.data
}

// Award-slip status transition (Draft → Active → Locked → Published).
export const setAwardSlipStatus = async (awardSlipId: string, status: string) => {
  const res = await apiClient({
    method: "PATCH",
    url: `/assessment/award-slip/${awardSlipId}/status`,
    data: { status },
  })
  return res.data
}

// ── Final Term carryover ───────────────────────────────────────────────────

export type FinalTermPct = {
  termId: string
  termName: string
  percentage: number
  isFinal: boolean
}

export type FinalTermRow = {
  student: { id: string; roll_number: string; full_name: string; gr_number: string }
  prior: Record<string, number | "AB" | "NA" | null>
  final_exam_total: number | "AB" | "NA" | null
  // The persisted carryover blend for this student (null until recomputed).
  stored: { combined_final_term: number } | null
}

export type FinalTermPrep = {
  course_structure: { id: string; subject_name: string; total_marks: number }
  term_percentages: FinalTermPct[]
  breakdown: { id: string; status: string }
  subcategories: { _id: string; description: string; marks: number }[]
  rows: FinalTermRow[]
}

export const getFinalTermPrep = async (params: {
  courseStructureId: string
  breakdownId: string
}): Promise<{ success: boolean; data: FinalTermPrep }> => {
  const res = await apiClient({
    method: "GET",
    url: appendQueryParams(`${BASE}/final-term-prep`, params),
  })
  return res.data
}

// Recompute + persist the weighted Final Term blend for all students of a subject.
export const recomputeFinalTerm = async (data: {
  courseStructureId: string
  breakdownId: string
}): Promise<{ success: boolean; message: string; data: { updated: number } }> => {
  const res = await apiClient({ method: "POST", url: `${BASE}/final-term/recompute`, data })
  return res.data
}
